<template>
  <b-card
    no-body
    class="report-heatmap"
  >
    <b-card-body>
      <div class="d-flex justify-content-between align-items-start mb-1">
        <div>
          <h4 class="mb-25">
            {{ title }}
          </h4>
          <div class="report-heatmap__subtitle">
            {{ subtitle }}
          </div>
        </div>

        <!-- راهنمای مقیاس: روی مقیاس پیوسته اجباری است -->
        <div
          v-if="max"
          class="report-heatmap__legend"
        >
          <span class="report-heatmap__legend-label">۰</span>
          <span
            v-for="(color, index) in ramp"
            :key="index"
            class="report-heatmap__swatch"
            :style="{ background: color }"
          />
          <span class="report-heatmap__legend-label">{{ max }}</span>
        </div>
      </div>

      <div
        class="report-heatmap__body"
        :class="{ 'report-heatmap__body--busy': loading }"
      >
        <div
          v-if="max"
          class="report-heatmap__scroll"
        >
          <table class="report-heatmap__grid">
            <thead>
              <tr>
                <th />
                <th
                  v-for="hour in hours"
                  :key="`h-${hour}`"
                  class="report-heatmap__hour"
                >
                  <!-- هر ۳ ساعت برچسب، وگرنه ۲۴ عدد روی هم می‌افتد -->
                  {{ hour % 3 === 0 ? hour : '' }}
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(day, dayIndex) in days"
                :key="`d-${dayIndex}`"
              >
                <th class="report-heatmap__day">
                  {{ day }}
                </th>
                <td
                  v-for="hour in hours"
                  :key="`c-${dayIndex}-${hour}`"
                  class="report-heatmap__cell-wrap"
                >
                  <div
                    v-b-tooltip.hover
                    class="report-heatmap__cell"
                    :style="{ background: colorOf(valueAt(dayIndex, hour)) }"
                    :title="`${day} ساعت ${hour} — ${valueAt(dayIndex, hour)} سفارش`"
                  />
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <div
          v-else
          class="report-heatmap__empty"
        >
          <feather-icon
            icon="GridIcon"
            size="26"
          />
          <p class="mt-1 mb-0">
            داده‌ای برای این بازه وجود ندارد
          </p>
        </div>

        <!-- بیشترین تمرکز، به‌عنوان برداشت آماده -->
        <div
          v-if="peak"
          class="report-heatmap__peak mt-1"
        >
          <feather-icon
            icon="TrendingUpIcon"
            size="13"
          />
          <span class="ml-25">
            اوج سفارش: {{ days[peak.Day] }} ساعت {{ peak.Hour }} ({{ peak.Value }} سفارش)
          </span>
        </div>
      </div>
    </b-card-body>
  </b-card>
</template>

<script>
import { BCardBody, VBTooltip } from 'bootstrap-vue'
import { sequential, ink } from './theme'

export default {
  name: 'ReportHeatmap',
  components: { BCardBody },
  directives: { 'b-tooltip': VBTooltip },
  props: {
    // آرایه‌ی ReportModel.HeatCell
    cells: {
      type: Array,
      default: () => [],
    },
    title: {
      type: String,
      default: 'الگوی زمانی سفارش',
    },
    subtitle: {
      type: String,
      default: 'توزیع سفارش روی روز هفته و ساعت — برای زمان‌بندی کمپین و پشتیبانی',
    },
    loading: {
      type: Boolean,
      default: false,
    },
    skin: {
      type: String,
      default: 'light',
    },
  },
  data() {
    return {
      days: ['شنبه', 'یکشنبه', 'دوشنبه', 'سه‌شنبه', 'چهارشنبه', 'پنجشنبه', 'جمعه'],
      hours: Array.from({ length: 24 }, (_, i) => i),
    }
  },
  computed: {
    /** دسترسی O(1) به مقدار هر خانه */
    index() {
      const map = {}
      this.cells.forEach(cell => {
        map[`${cell.Day}-${cell.Hour}`] = cell.Value
      })
      return map
    },

    max() {
      return this.cells.reduce((acc, c) => Math.max(acc, c.Value), 0)
    },

    peak() {
      if (!this.max) return null
      return this.cells.reduce((acc, c) => (c.Value > (acc ? acc.Value : 0) ? c : acc), null)
    },

    ramp() {
      return sequential(this.skin)
    },
  },
  methods: {
    valueAt(day, hour) {
      return this.index[`${day}-${hour}`] || 0
    },

    /**
     * رمپ ترتیبی تک‌رنگ. خانه‌ی صفر عمداً به رنگ سطح نزدیک است — «هیچ»
     * باید مثل هیچ خوانده شود، نه مثل کم‌ترین مقدار.
     */
    colorOf(value) {
      if (!value) return this.skin === 'dark' ? '#323a52' : '#f3f2f7'

      const position = Math.min(
        Math.floor((value / this.max) * this.ramp.length),
        this.ramp.length - 1,
      )

      return this.ramp[position]
    },
  },
}
</script>

<style lang="scss" scoped>
.report-heatmap {
  height: 100%;

  &__subtitle {
    font-size: 0.75rem;
    color: #b9b9c3;
  }

  &__body {
    transition: opacity 0.15s ease;

    &--busy {
      opacity: 0.45;
    }
  }

  // جدول پهن داخل خودش اسکرول می‌شود تا صفحه افقی نشود
  &__scroll {
    overflow-x: auto;
  }

  &__grid {
    border-collapse: separate;
    border-spacing: 2px;
    width: 100%;
    min-width: 34rem;
  }

  &__hour,
  &__day {
    font-size: 0.65rem;
    font-weight: 400;
    color: #b9b9c3;
    text-align: center;
    font-variant-numeric: tabular-nums;
  }

  &__day {
    text-align: left;
    padding-left: 0.35rem;
    white-space: nowrap;
  }

  &__cell-wrap {
    padding: 0;
  }

  &__cell {
    height: 15px;
    border-radius: 3px;
    cursor: default;
    transition: transform 0.1s ease;

    &:hover {
      transform: scale(1.25);
    }
  }

  &__legend {
    display: flex;
    align-items: center;

    &-label {
      font-size: 0.65rem;
      color: #b9b9c3;
      margin: 0 0.35rem;
      font-variant-numeric: tabular-nums;
    }
  }

  &__swatch {
    width: 12px;
    height: 10px;
    display: inline-block;

    &:first-of-type {
      border-radius: 2px 0 0 2px;
    }

    &:last-of-type {
      border-radius: 0 2px 2px 0;
    }
  }

  &__peak {
    font-size: 0.75rem;
    color: #b9b9c3;
  }
}
</style>
